import React, {
	ChangeEvent,
	useEffect,
	useRef,
	useState,
} from 'react';
import { GeneratedContent } from '../../stores/types/content';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { saveMediaItem } from '../../stores/actions/media';
import { MediaItem } from '../../stores/reducers/media';
import { GraphicInterface } from '../../stores/reducers/graphics';
import {editGraphicsAction} from "../../stores/actions/graphics";
import Icon, {COLORS, ICONS} from "../general-components/icon";
import {checkFileName} from "../../utils/media";

interface GenerateContentTitleProps {
	generatedContent?: GeneratedContent;
	saveCallback?: (newString: string) => void;
	mediaItem?: MediaItem;
	graphic?: GraphicInterface;
	title: string;
	placeholder?: string;
	allowBlank?: boolean;
}

export default function GenerateContentTitle(props: GenerateContentTitleProps) {
	const {
		generatedContent,
		saveCallback,
		mediaItem,
		graphic,
		title,
		placeholder = '',
		allowBlank = false
	} = props;
	const dispatch = useDispatch();

	const [mouseIsOver, setMouseIsOver] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [contentTitle, setContentTitle] = useState<string>(title);
	const input = useRef<HTMLInputElement>(null);

	const token = useSelector((s: GS) => s.SessionReducer.token);


	useEffect(() => {
		if (isEditing) {
			if (input.current) {
				input.current.focus();
			}
		}
	}, [isEditing]);

	useEffect(() => {
		if (!isEditing && title !== contentTitle) {
			// assume the title changed from outside the component (tab switch or title edit)
			setContentTitle(title);
		}
	}, [title])

	const updateTitle = (e: ChangeEvent<HTMLInputElement>) => {
		setContentTitle(e.target.value);
	}

	const saveTitle = () => {
		setIsEditing(false);

		if (checkFileName(contentTitle, false, allowBlank)) {
			if (saveCallback) {
				saveCallback(contentTitle);
			} else if (mediaItem) {
				dispatch(saveMediaItem(
					token ?? '',
					{
						...mediaItem,
						custom_name: contentTitle,
					},
					undefined,
				));
			} else if (graphic) {
				dispatch(editGraphicsAction(
					token ?? '',
					{
						...graphic,
						title: contentTitle,
					},
					() => {
					},
				));
			}
		}
	}

	return (
		<div
			onMouseEnter={() => setMouseIsOver(true)}
			onMouseLeave={() => setMouseIsOver(false)}
			onClick={() => {
				setIsEditing(true);
			}}
			className={classNames(
				'generate-content-title-title',
				{ 'generate-content-title-hover': mouseIsOver && !isEditing },
				{ 'generate-content-title-normal': !mouseIsOver && !isEditing },
				{ 'generate-content-title-editing': isEditing },
			)}
		>
			<div
				className={classNames(
					'text',
					'truncate-overflow',
					'generate-content-title-text',
				)}
			>
				{!isEditing && (contentTitle ? contentTitle : placeholder)}
				{isEditing && (
					<input
						id={`generate-content-title-${generatedContent?.id}`}
						type="text"
						value={contentTitle}
						onChange={updateTitle}
						onBlur={saveTitle}
						ref={input}
						placeholder={placeholder}
					/>

				)}
			</div>
			{(isEditing || mouseIsOver) && (
				<div
					className={isEditing
						? 'generate-content-title-checkmark'
						: 'generate-content-title-pencil'
					}
					onClick={() => {
						if (input.current) {
							if (isEditing) {
								input.current.blur();
							} else {
								input.current.focus();
							}
						}
					}}
				>
					<Icon
						name={isEditing ? ICONS.CHECK_V2 : ICONS.EDIT}
						size={16}
						color={isEditing ? COLORS.WHITE : COLORS.TEXT_GRAY_400}
					/>
				</div>
			)}
		</div>
	);
}